// Multiple Charts System Types
// Architecture: v4.0.0 Multiple Charts Implementation
// Reference: Clean implementation without legacy support

import { ChartMetadata } from '../models/storage';
import { ParsedChartData } from '../models/types';
import type { ChartViewMode } from './ChartViewTypes';

/**
 * Maximum number of history snapshots per chart
 */
export const MAX_CHART_HISTORY = 30;

/**
 * Constant for current/live state in history navigation
 * -1 means "viewing the latest state" (not a specific historical snapshot)
 */
export const CURRENT_LIVE_INDEX = -1;

/**
 * Helper: Check if history index represents current/live state
 */
export const isCurrentLiveState = (index: number): boolean =>
  index === CURRENT_LIVE_INDEX;

/**
 * Helper: Get effective array index from history index
 * Converts CURRENT_LIVE_INDEX (-1) to actual last index
 */
export const getEffectiveHistoryIndex = (index: number, historyLength: number): number =>
  index === CURRENT_LIVE_INDEX ? historyLength - 1 : index;

/**
 * Chart marker interface
 * For chart annotations and visual markers
 */
export interface ChartMarker {
  id: string;
  text: string;
  name?: string;
  position: { x: number; y: number };
  visible: boolean;
}

/**
 * Snapshot source configuration - single source of truth
 */
export const SNAPSHOT_SOURCES = {
  'original': { color: 'green' },
  'degree-move': { color: 'blue' },
  'time-move': { color: 'orange' },
  'new-time': { color: 'purple' },
  'duplicate': { color: 'gray' },
} as const;

export type SnapshotSource = keyof typeof SNAPSHOT_SOURCES;

/**
 * Snapshot of chart state at a specific time
 * Used for undo/redo and time navigation history (planet drag, time edits, etc.)
 */
export interface ChartStateSnapshot {
  id: string;                    // unique snapshot ID (timestamp-based)
  apiResponse: any;              // raw API response at this time
  chartData: ParsedChartData;    // parsed chart data
  createdAt: Date;               // when this snapshot was created (user action time)
  chartDateTime: string;         // the chart's datetime ISO string (the time being calculated for)
  label?: string;                // optional label ("Original", "+5° Lg", etc.)
  source: SnapshotSource;        // how this state was created
}

/**
 * Individual chart instance with isolated state
 * Each chart maintains its own settings and UI state
 */
export interface ChartInstance {
  id: string;                          // hash-based unique ID (location+datetime+timezone)
  metadata: ChartMetadata;             // chart metadata (name, location, datetime, etc.)
  chartData: ParsedChartData;          // parsed chart data from API
  apiResponse: any;                    // raw API response
  
  // Instance-specific state (isolated per chart)
  markers: ChartMarker[];              // chart-specific markers
  activePresetId: string | null;       // active color preset for this chart
  zoomLevel: number;                   // zoom level state
  displayMode: 'symbols' | 'short';    // current display mode
  panPosition: { x: number; y: number }; // pan position state
  settings?: {                         // chart-specific settings
    enableAnimations?: boolean;        // planet animation enable/disable
    colorPreset?: string;              // color preset for this chart
    showGrahaChakra?: boolean;         // graha chakra visibility toggle
    showMuhurtaChakra?: boolean;       // muhurta chakra visibility toggle
    muhurtaStartPoint?: string;        // muhurta start point (sunrise, planet, lagna)
    muhurtaCustomSettings?: unknown;   // custom muhurta settings
    showAshtamshaChakra?: boolean;     // D8 Ashtamsha chakra visibility toggle
    showNakshatraChakra?: boolean;     // Nakshatra chakra visibility toggle
    nakshatraStartPoint?: string;      // nakshatra start point
    nakshatraCustomSettings?: unknown; // custom nakshatra settings
    showNakshatraPada?: boolean;       // Nakshatra Pada divisions visibility toggle (depends on showNakshatraChakra)

    // Universal divisional chakras settings
    showD7Chakra?: boolean;            // D7 Saptamsa chakra visibility
    d7StartPoint?: string;             // D7 start point (sunrise, planet, lagna)
    showD8Chakra?: boolean;            // D8 chakra visibility (alternative to showAshtamshaChakra)
    d8StartPoint?: string;             // D8 start point
    showD12Chakra?: boolean;           // D12 Dwadasamsa chakra visibility
    d12StartPoint?: string;            // D12 start point
    showD20Chakra?: boolean;           // D20 Vimsamsa chakra visibility
    d20StartPoint?: string;            // D20 start point
    showD24Chakra?: boolean;           // D24 Siddhamsa chakra visibility
    d24StartPoint?: string;            // D24 start point
    showD30Chakra?: boolean;           // D30 Trimsamsa chakra visibility
    d30StartPoint?: string;            // D30 start point
    showD40Chakra?: boolean;           // D40 Khavedamsa chakra visibility
    d40StartPoint?: string;            // D40 start point
    showD60Chakra?: boolean;           // D60 Shashtiamsa chakra visibility
    d60StartPoint?: string;            // D60 start point

    // View mode
    activeView?: ChartViewMode;
    horaClockDesign?: Record<string, any>; // hora clock design config

    // Planet drag movement settings (per draggable planet: Lg, Sy, Ch)
    movementStepDegrees?: Record<string, number>;  // { Lg: 1, Sy: 1, Ch: 1 }
  };
  
  // UI state tracking
  isLoading: boolean;                  // individual chart loading state
  lastAccessed: Date;                  // timestamp of last activation

  // Accordion states (DataSections)
  accordionStates: Record<string, boolean>; // expanded/collapsed states per section

  // History management (up to MAX_CHART_HISTORY snapshots)
  history: ChartStateSnapshot[];       // array of historical states
  currentHistoryIndex: number;         // CURRENT_LIVE_INDEX (-1) = current/live state, 0+ = viewing history
}

/**
 * Tab metadata for lazy loading - minimal info needed to display tab
 */
export interface TabMeta {
  id: string;                          // chart ID
  name: string;                        // display name for tab
  lastAccessed?: Date;                 // for sorting/LRU
}

/**
 * Workspace state containing loaded charts and layout
 * Single workspace with tabbed interface
 * Supports lazy loading: tabs visible immediately, data loaded on demand
 */
export interface WorkspaceState {
  id: string;                          // workspace identifier
  name: string;                        // workspace display name
  charts: string[];                    // array of chart IDs (tabs, may not be loaded yet)
  activeChartId: string | null;        // currently active/visible chart
  chartLayout: 'single';               // layout mode (only single for v4.0.0)

  // Workspace metadata
  created: Date;                       // creation timestamp
  modified: Date;                      // last modification timestamp
  accessed: Date;                      // last access timestamp
  isDefault: boolean;                  // default workspace flag

  // Tab ordering and UI state
  tabOrder: string[];                  // order of tabs (chart IDs)
  tabMeta: Record<string, TabMeta>;    // metadata for each tab (for lazy loading UI)
  maxOpenCharts: number;               // maximum charts allowed (default: 5)
}

/**
 * Multiple Charts Context Interface
 * Main state management for multiple charts system
 */
export interface MultipleChartsContextType {
  // Charts state management
  charts: Map<string, ChartInstance>;           // all loaded chart instances
  activeChartId: string | null;                // currently active chart ID
  loadedChartIds: string[];                    // array of loaded chart IDs (for tabs)
  
  // Loading state management
  isLoading: boolean;                          // global loading state
  loadingCharts: Set<string>;                  // individual chart loading states
  
  // Chart operations
  loadChart: (chartId: string, options?: ChartLoadingOptions, preloadedData?: PreloadedChartData) => Promise<void>; // load single chart
  loadChartFromData: (chartId: string, chartData: ParsedChartData, metadata: any, options?: ChartLoadingOptions) => Promise<void>; // load chart from data directly (for share mode)
  loadMultipleCharts: (chartIds: string[]) => Promise<void>;  // load multiple charts
  unloadChart: (chartId: string) => void;                     // unload chart and cleanup
  switchToChart: (chartId: string) => void;                   // switch active chart
  
  // Chart data access
  getActiveChart: () => ChartInstance | null;                 // get active chart instance
  getChart: (chartId: string) => ChartInstance | null;        // get specific chart
  getAllLoadedCharts: () => ChartInstance[];                  // get all loaded charts
  
  // Chart state updates
  updateChart: (chartId: string, updates: Partial<ChartInstance>) => void;
  updateChartData: (chartId: string, chartData: ParsedChartData) => void;
  updateChartMarkers: (chartId: string, markers: ChartMarker[]) => void;
  updateChartPreset: (chartId: string, presetId: string) => void;
  
  // Workspace management
  workspace: WorkspaceState;                                  // current workspace state
  saveWorkspace: () => Promise<void>;                         // save workspace to storage
  loadWorkspace: (workspaceId?: string) => Promise<void>;     // load workspace from storage
  
  // Tab management
  reorderTabs: (fromIndex: number, toIndex: number) => void;  // reorder chart tabs
  closeTab: (chartId: string) => void;                        // close chart tab
  duplicateChart: (chartId: string) => Promise<string>;       // duplicate chart in new tab
  addCurrentTimeSnapshot: (chartId: string) => Promise<void>; // recalculate chart for current time, add as snapshot
  
  // History management (for planet drag, time edits, etc.)
  addChartSnapshot: (chartId: string, snapshot: Omit<ChartStateSnapshot, 'id'>) => void;  // add new snapshot
  navigateHistory: (chartId: string, index: number) => void;      // navigate to specific history index (-1 = current)
  getChartHistory: (chartId: string) => ChartStateSnapshot[];     // get history for chart
  clearChartHistory: (chartId: string) => void;                   // clear all history for chart
  deleteChartSnapshot: (chartId: string, snapshotIndex: number) => void;  // delete specific snapshot (not original/active)
  undoChartState: (chartId: string) => void;                      // go to previous state
  redoChartState: (chartId: string) => void;                      // go to next state
}

/**
 * Chart Loading Options
 * Configuration for loading charts into workspace
 */
export interface ChartLoadingOptions {
  makeActive?: boolean;                // set as active chart after loading
  insertAtIndex?: number;              // specific position in tab order
  duplicateId?: string;                // source chart for duplication
  preserveState?: boolean;             // preserve UI state when loading
}

/**
 * Pre-loaded chart data to skip redundant storage calls (DRY optimization)
 */
export interface PreloadedChartData {
  chartData: import('../models/types').ParsedChartData;
  metadata: any;
  history?: ChartStateSnapshot[];
  currentHistoryIndex?: number;
}

/**
 * Chart Tab Configuration
 * UI configuration for chart tabs
 */
export interface ChartTabConfig {
  chartId: string;                     // chart identifier
  title: string;                       // tab display title (max 30 chars)
  isActive: boolean;                   // active tab indicator
  isLoading: boolean;                  // loading indicator
  canClose: boolean;                   // closeable tab flag
  hasUnsavedChanges: boolean;          // unsaved changes indicator
  lastAccessed: Date;                  // for sorting/organization
}

/**
 * Workspace Events
 * Event types for workspace management
 */
export type WorkspaceEventType = 
  | 'workspace.chart.loaded'           // chart was loaded into workspace
  | 'workspace.chart.unloaded'         // chart was unloaded from workspace
  | 'workspace.chart.activated'        // chart was made active
  | 'workspace.layout.changed'         // workspace layout changed
  | 'workspace.saved'                  // workspace was saved
  | 'workspace.restored';              // workspace was restored

export interface WorkspaceEvent {
  type: WorkspaceEventType;
  workspaceId: string;
  chartId?: string;
  timestamp: Date;
  data?: any;
}

/**
 * Chart ID Generation Utilities
 * Hash-based chart identification
 */
export interface ChartIdComponents {
  location: string;                    // normalized location string
  dateTime: string;                    // ISO datetime string
  timezone: string;                    // timezone identifier
}

export interface ChartIdResult {
  chartId: string;                     // generated chart ID
  components: ChartIdComponents;       // source components
  hash: string;                        // full SHA-256 hash
}